import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, MapPin, Phone, Mail, Clock } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../data/site";

const QUICK_LINKS = [
    { to: "/", label: "Home", testid: "footer-home-link" },
    { to: "/solutions", label: "Solutions", testid: "footer-solutions-link" },
    { to: "/why-pacific", label: "Why Pacific", testid: "footer-whypacific-link" },
    { to: "/contact", label: "Contact", testid: "footer-contact-link" },
    { to: "/drive", label: "Drive With Pacific", testid: "footer-drive-link" },
];

const SOCIALS = [
    { icon: Linkedin, label: "LinkedIn", testid: "footer-social-linkedin" },
    { icon: Facebook, label: "Facebook", testid: "footer-social-facebook" },
    { icon: Instagram, label: "Instagram", testid: "footer-social-instagram" },
];

export default function Footer() {
    return (
        <footer className="bg-abyss text-gray-300 border-t border-white/10">
            <div className="container-px py-16 lg:py-20">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <Logo testid="footer-logo" imgClass="h-14" />
                        <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-gray-400">
                            Southern California logistics, drayage, warehousing and
                            transportation solutions backed by dedicated fleet capacity.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {SOCIALS.map(({ icon: Icon, label, testid }) => (
                                <a
                                    key={label}
                                    href="#"
                                    data-testid={testid}
                                    aria-label={`Pacific Expressway on ${label}`}
                                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 text-gray-400 transition-colors hover:border-gold hover:text-gold"
                                >
                                    <Icon size={17} aria-hidden="true" />
                                </a>
                            ))}
                        </div>
                    </div>
                    <nav aria-label="Footer">
                        <h3 className="font-heading text-xs font-bold uppercase tracking-[0.28em] text-gold">
                            Quick Links
                        </h3>
                        <ul className="mt-6 space-y-3">
                            {QUICK_LINKS.map((link) => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        data-testid={link.testid}
                                        className="text-[15px] text-gray-300 transition-colors hover:text-gold"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div>
                        <h3 className="font-heading text-xs font-bold uppercase tracking-[0.28em] text-gold">
                            Get in Touch
                        </h3>
                        <ul className="mt-6 space-y-4 text-[15px]">
                            <li className="flex gap-3">
                                <MapPin size={17} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                <span data-testid="footer-address">
                                    {COMPANY.address.street}
                                    <br />
                                    {COMPANY.address.city}
                                </span>
                            </li>
                            <li>
                                <a
                                    href={COMPANY.phoneHref}
                                    data-testid="footer-phone"
                                    className="flex gap-3 transition-colors hover:text-gold"
                                >
                                    <Phone size={17} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                    {COMPANY.phone}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={COMPANY.emailHref}
                                    data-testid="footer-email"
                                    className="flex gap-3 break-all transition-colors hover:text-gold"
                                >
                                    <Mail size={17} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                    {COMPANY.email}
                                </a>
                            </li>
                            <li className="flex gap-3">
                                <Clock size={17} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                <span data-testid="footer-hours">
                                    {COMPANY.hours.map((h) => (
                                        <span key={h.days} className="block">
                                            {h.days}: {h.time}
                                        </span>
                                    ))}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="container-px flex flex-col items-center justify-between gap-3 py-6 sm:flex-row">
                    <p className="text-xs tracking-wide text-gray-500" data-testid="footer-copyright">
                        © 2026 Pacific Expressway. All Rights Reserved.
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-600 font-heading">
                        We Keep Your Containers Moving
                    </p>
                </div>
            </div>
        </footer>
    );
}
